import {OsmLevel, PaginationNetwork} from '../enumerator/network.enum';
import GraphQLService from './graphql';
import {networksByDistance, networksByName, networksCoordinates} from '../enumerator/graphql-query.enum';
import {TabsFilterNetworks, TabsTypeNetworks} from '../enumerator/tabs.enum';
import {agglosEnum} from '../enumerator/agglos.enum';
import GeolocationService from './geolocation';
import regeneratorRuntime from 'regenerator-runtime';
import ToasterService from './toaster';
import ToastSchema from '../schema/toaster.schema';
import Translate from '../../services/translate';

/**
 * -------------
 * AggloService
 * -------------
 * gère le service de recherche d'une agglomération
 * */
export default class AggloService {
    /** contient l'ensemble des networks avec leurs coordonnées (getAllAgglos) */
    static networksList = [];

    /** les tabs de osmLevel */
    static tabs = TabsTypeNetworks;

    /** les tabs de distance / nom */
    static tabsFilter = TabsFilterNetworks.map(item => ({...item}));

    /** la liste des elements */
    static arElements = [];

    /** la liste des elements filtrés */
    static arElementsFiltered = [];

    /** Objet initalisant le skeletor loader */
    static loading = {
        name: true,
        distance: true,
    };

    /** Le niveau de recherche (dept, urbain, region) */
    static osmLevel = AggloService.tabs.find(tab => tab.active).id;

    /** Id de la tab active */
    static idTabActive = 'name';

    /** Page active */
    static activePage = 0;

    /** Si on doit charger la page suivant */
    static hasNextPage;

    /** valeur de recherche */
    static searchValue = '';

    /** la position de l'utilisateur */
    static currentPosition;

    /** timeout pour la recherche */
    static timeoutSearch;

    /** charge les données au scroll */
    static scrolling = false;

    /**
     * -------------
     * initSearchAgglo
     * -------------
     * initialise la recherche par réseau (urbain/départementaux/régionaux)
     * */
    static initSearchAgglo = function () {
        // init
        AggloService.initTabs();
        GeolocationService.getPosition()
            .then(function (position) {
                AggloService.currentPosition = position;
            })
            .finally(() => {
                AggloService.filterAggloByTabs()
                    .then(() => {
                        AggloService.getListNetworksByFilter(AggloService.idTabActive)
                            .then(function (networks) {
                                AggloService.setData(networks);
                                AggloService.loadData();
                            });
                    });
            });

        // listeners
        AggloService.returnCallBackTabs();
        AggloService.inputAggloValueChange();
        AggloService.changeFilter();
    }

    /**
     * ---------------
     * initHomeSearchAgglo
     * ---------------
     * initialise la recherche par réseau sur la homepage
     * */
    static initHomeSearchAgglo = function () {
        AggloService.inputHomeAggloValueChange();
    }

    /**
     * ---------------
     * getAllAgglos
     * ---------------
     * récupère toutes les agglos avec leur coordonnées
     */
    static getAllAgglos = async function (offset = 0, osmLevel = OsmLevel.URBAIN) {
        if (agglosEnum.length !== 0) { // TODO: temporaire, doit être remplacé par un transient
            AggloService.networksList = agglosEnum;
            return;
        }
        let queryList = {};
        let queryParams = {};

        queryParams = {
            '$osmLevel': osmLevel,
            '$limit': PaginationNetwork.ELMT_PER_PAGE,
            '$search': '""',
            '$offset': offset
        }
        queryList = GraphQLService.setQueryVar(networksCoordinates, queryParams);

        let result = await GraphQLService.getQuery(queryList);
        result = JSON.parse(result);

        $.each(result.data.networks.networks, function (index, item) {
            item.osmLevel = osmLevel;
            AggloService.networksList.push(item);
        });

        if (result.data.networks.pagination.hasNextPage && offset <= result.data.networks.pagination.totalCount) {
            await AggloService.getAllAgglos(offset + PaginationNetwork.ELMT_PER_PAGE, osmLevel);
        } else { // récupère tous les types de osmLevel
            osmLevel -= 2;
            if (osmLevel > 2) {
                await AggloService.getAllAgglos(0, osmLevel);
            }
        }
    }

    /**
     * -------------------
     * filterAggloByTabs
     * -------------------
     * Init les tabs de filtre si on a la position
     */
    static filterAggloByTabs = async function () {
        if (AggloService.currentPosition !== undefined) {
            AggloService.tabsFilter = await AggloService.tabsFilter.map(tabFilter => {
                return {
                    ...tabFilter,
                    active: tabFilter.id === 'distance'
                };
            });
            AggloService.idTabActive = 'distance';
        } else {
            $('[data-network-filter]').removeClass('active');
            $('[data-network-filter="name"]').addClass('active');
            AggloService.idTabActive = 'name';
            const index = AggloService.tabsFilter.findIndex(tab => tab.id === 'distance');
            AggloService.tabsFilter[index].disabled = true;
        }
    }

    /**
     * -------------------
     * getListNetworksByFilter
     * -------------------
     * Permet de filtrer la liste selon l'élement du filtre séléctionné (ex: distance, nom etc.)
     */
    static getListNetworksByFilter = function (filter, resetData = false) {
        AggloService.loading[filter] = !AggloService.arElements?.length;

        return new Promise((resolve, reject) => {
            let queryList = {};
            let queryParams = {};

            switch (filter) {
                case 'distance':
                    queryParams = {
                        '$coord': "[" + AggloService.currentPosition.longitude + ", " + AggloService.currentPosition.latitude + "]",
                        '$osmLevel': AggloService.osmLevel,
                        '$limit': PaginationNetwork.ELMT_PER_PAGE,
                        '$offset': AggloService.activePage,
                        '$search': '"' + AggloService.searchValue + '"',
                    }
                    queryList = GraphQLService.setQueryVar(networksByDistance, queryParams);

                    return GraphQLService.getQuery(queryList)
                        .then((networks) => {
                            AggloService.formatNetworks(networks, resetData);
                            resolve(AggloService.arElements);
                        })
                        .catch(reason => {
                            const content = {
                                title: Translate.print('toaster.graphql.erreur'),
                                text: reason,
                                status: 'alert',
                            }
                            ToasterService.showToaster(new ToastSchema(content));
                            reject(reason);
                        });

                case 'name':
                    queryParams = {
                        '$osmLevel': AggloService.osmLevel,
                        '$limit': PaginationNetwork.ELMT_PER_PAGE,
                        '$search': '"' + AggloService.searchValue + '"',
                        '$offset': AggloService.activePage
                    }
                    queryList = GraphQLService.setQueryVar(networksByName, queryParams);

                    return GraphQLService.getQuery(queryList)
                        .then((networks) => {
                            AggloService.formatNetworks(networks, resetData);
                            resolve(AggloService.arElements);
                        })
                        .catch(async (reason) => {
                            const content = {
                                title: Translate.print('toaster.graphql.erreur'),
                                text: reason,
                                status: 'alert',
                            }
                            ToasterService.showToaster(new ToastSchema(content));
                            reject(reason);
                        });
            }
        });
    }

    /**
     * ----------------------
     * formatNetworks
     * ----------------------
     * formate les données reçues
     */
    static formatNetworks = function (networks, resetData) {
        if (resetData) {
            AggloService.arElements = [];
        }
        const networksList = networks.data.networks;

        $.each(networksList.networks, function (index, item) {
            if ( !AggloService.arElements.includes(item.id) ) {
                AggloService.arElements.push(item);
            }
        });

        if (networksList.pagination) {
            AggloService.hasNextPage = networksList.pagination.hasNextPage;
        }
        AggloService.loading[AggloService.idTabActive] = false;
    }

    /**
     * -------------------
     * inputAggloValueChange
     * -------------------
     * Permet de retourner la liste des agglomérations filtrées par la valeur de la search bar
     */
    static inputAggloValueChange = async function () {
        $(document).on('input', '[data-network-search]', async function () {
            clearTimeout(AggloService.timeoutSearch);
            AggloService.timeoutSearch = setTimeout(async () => {
                AggloService.activePage = 0;
                AggloService.searchValue = $(this).val();
                AggloService.getListNetworksByFilter(AggloService.idTabActive, true)
                    .then(function (networks) {
                        AggloService.setData(networks);
                    });
            }, 500);
        });
    }

    /**
     * -------------------------
     * inputHomeAggloValueChange
     * -------------------------
     * Permet de retourner la liste des agglomérations filtrées par la valeur de la search bar
     * Effectue la recherche sur l'ensemble des osmLevel (homepage)
     */
    static inputHomeAggloValueChange = function () {
        $(document).on('input', '[data-network-search]', function () {
            clearTimeout(AggloService.timeoutSearch);
            AggloService.activePage = 0;
            AggloService.searchValue = $(this).val();
            AggloService.osmLevel = OsmLevel.REGION;

            AggloService.timeoutSearch = setTimeout(async () => {
                AggloService.getListNetworksByFilter(AggloService.idTabActive, true)
                    .then(function () {
                        AggloService.osmLevel = OsmLevel.DEPT;
                        AggloService.getListNetworksByFilter(AggloService.idTabActive, false)
                            .then(function () {
                                AggloService.osmLevel = OsmLevel.URBAIN;
                                AggloService.getListNetworksByFilter(AggloService.idTabActive, false)
                                    .then(function (networks) {
                                        AggloService.setData(networks);
                                    });
                            });
                    });
            }, 500);
            ($(this).val() !== '') ?
                $('[data-network-autocomplete]').trigger('input-fill', '[data-network-search]') :
                $('[data-network-autocomplete]').trigger('input-empty', '[data-network-search]');
        });

        $(document).on('submit', '[data-home-network]', function (event) {
            event.preventDefault();
        });
    }

    /**
     * ------------------------
     * setData
     * ------------------------
     * Affiche la liste des réseaux filtrés dans le DOM
     */
    static setData = function (networks) {
        $('[data-network-autocomplete-list]').children().remove();
        $.each(networks, function (index, item) {
            $('[data-network-autocomplete-list]').append(
                '<li class="resultsReseauxItem">' +
                '<a target="_blank" href="https://maps.mybus.io/' + item.id + '">' + item.name + '</a>' +
                '</li>'
            )
        });
    }

    /**
     * -------------
     * initTabs
     * -------------
     * Initialise les données des tabs en front
     * */
    static initTabs = function () {
        $.each(TabsTypeNetworks, function (index, item) {
            $('[data-network-tab]').each(function () {
                if (parseInt($(this).attr('data-network-tab')) === index) {
                    $(this).attr('data-network-tab', item.id);
                }
            })
        })
    }

    /**
     * -------------------
     * returnCallBackTabs
     * -------------------
     * Permet de détecter lorsque l'on clique sur un tabs
     */
    static returnCallBackTabs = function () {
        $(document).on('click', '[data-network-tab]', function () {
            const selectedTab = parseInt($(this).attr('data-network-tab'));

            $('[data-network-tab]').removeClass('active');
            $(this).addClass('active');
            AggloService.osmLevel = selectedTab;
            AggloService.changeTabs().then(() => {
            });
        })
    }

    /**
     * -------------------
     * changeTabs
     * -------------------
     * Récupère les réseaux au changement de tabs
     */
    static changeTabs = async function () {
        AggloService.activePage = 0;
        AggloService.arElements = [];
        AggloService.arElementsFiltered = [];
        AggloService.searchValue = '';
        $('[data-network-search]').val('');
        AggloService.getListNetworksByFilter(AggloService.idTabActive, true)
            .then(function (networks) {
                AggloService.setData(networks);
            });
    }

    /**
     * ------------------------
     * loadData
     * ------------------------
     * Charge la prochaine page de network
     */
    static loadData = function () {
        if (Theme.page_list.CURRENT_PAGE === Theme.page_list.PAGE_VILLE) {
            $('[data-network-autocomplete-list]').scroll(async function () {
                if (AggloService.hasNextPage && !AggloService.scrolling) {
                    AggloService.scrolling = true;
                    AggloService.activePage += PaginationNetwork.ELMT_PER_PAGE;
                    AggloService.getListNetworksByFilter(AggloService.idTabActive)
                        .then(function (networks) {
                            AggloService.scrolling = false;
                            AggloService.setData(networks);
                        });
                }
            });
        }
    }

    /**
     * ---------------
     * changeFilter
     * ---------------
     * Change le mode d'affichage
     */
    static changeFilter = function () {
        $(document).on('click', '[data-network-filter]', function () {
            const type = $(this).attr('data-network-filter');

            if (AggloService.currentPosition !== undefined || type === 'name') {
                $('[data-network-filter]').removeClass('active');
                $(this).addClass('active');
                type === 'distance' ? AggloService.idTabActive = 'distance' : AggloService.idTabActive = 'name';
                AggloService.changeTabs();
            }

            if (AggloService.currentPosition === undefined) {
                const content = {
                    title: Translate.print('geolocalisation.error.title'),
                    text: Translate.print('geolocalisation.error.activate'),
                    status: 'warning',
                };
                ToasterService.showToaster(new ToastSchema(content));
            }
        });
    }
}